import { defineStore } from 'pinia'
import type { Product, WishlistItem } from '~/types'

export const useWishlistStore = defineStore('wishlist', () => {
  const { get, post, del } = useApi()
  const toast = useToast()
  const shopPath = useShopPath()

  const items = ref<WishlistItem[]>([])
  const loading = ref(false)

  const productIds = computed(() => items.value.map(i => i.productId))

  function isInWishlist(productId: number) {
    return productIds.value.includes(productId)
  }

  async function fetchWishlist() {
    if (!useAuthStore().isAuthenticated) return
    loading.value = true
    try {
      const response = await get<WishlistItem[]>('/wishlist')
      if (response.success && response.data) {
        items.value = response.data
      }
    } finally {
      loading.value = false
    }
  }

  async function addToWishlist(product: Product) {
    if (!useAuthStore().isAuthenticated) {
      await navigateTo(shopPath('/cont/login'))
      return
    }
    const response = await post<WishlistItem>('/wishlist', { productId: product.id })
    if (response.success && response.data) {
      items.value.push(response.data)
      toast.add({ title: 'Adăugat la favorite', color: 'success' })
    }
  }

  async function removeFromWishlist(productId: number) {
    await del(`/wishlist/${productId}`)
    items.value = items.value.filter(i => i.productId !== productId)
    toast.add({ title: 'Eliminat din favorite', color: 'neutral' })
  }

  function removeByProductIds(productIds: number[]) {
    if (!productIds.length) return
    const reserved = new Set(productIds)
    items.value = items.value.filter((item) => !reserved.has(item.productId))
  }

  async function toggleWishlist(product: Product) {
    if (isInWishlist(product.id)) {
      await removeFromWishlist(product.id)
    } else {
      await addToWishlist(product)
    }
  }

  return {
    items,
    loading,
    productIds,
    isInWishlist,
    fetchWishlist,
    addToWishlist,
    removeFromWishlist,
    removeByProductIds,
    toggleWishlist,
  }
})
